import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Building2, CreditCard, TrendingUp, 
  Activity, Loader2, ArrowUpRight, Calendar, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/events`)
      ]);
      setStats(statsRes.data);
      setRecentEvents(eventsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Use mock data for demo if API fails
      setStats({
        total_organizers: 5,
        total_attendees: 150,
        total_users: 155,
        total_events: 8,
        active_events: 6,
        total_collected: 75000,
        pending_payments: 12
      });
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Organizers', 
      value: stats?.total_organizers || 0, 
      icon: UserCheck, 
      change: '+2 this month',
      color: 'from-blue-500 to-blue-600',
      link: '/admin/organizers'
    },
    { 
      title: 'Total Attendees', 
      value: stats?.total_attendees || 0, 
      icon: Users, 
      change: '+45 this week',
      color: 'from-purple-500 to-purple-600'
    },
    { 
      title: 'Active Events', 
      value: stats?.active_events || 0, 
      icon: Calendar, 
      change: `of ${stats?.total_events || 0} total`,
      color: 'from-green-500 to-green-600',
      link: '/admin/events'
    },
    { 
      title: 'Total Collected', 
      value: `₱${(stats?.total_collected || 0).toLocaleString()}`, 
      icon: CreditCard, 
      change: 'Platform-wide',
      color: 'from-orange-500 to-orange-600'
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Platform Overview</h1>
          <p className="text-gray-400 mt-1">
            batchm8s Admin Dashboard
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => (
            <Card 
              key={stat.title} 
              className="bg-gray-800 border-gray-700 overflow-hidden hover:bg-gray-750 transition-colors cursor-pointer"
              onClick={() => stat.link && window.location.assign(stat.link)}
              data-testid={`admin-stat-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">{stat.title}</p>
                    <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <ArrowUpRight className="h-4 w-4 text-green-400" />
                      <span className="text-sm text-green-400">{stat.change}</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Events & Payments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Recent Events</CardTitle>
                  <CardDescription className="text-gray-400">Newly created reunion events</CardDescription>
                </div>
                <Link to="/admin/events">
                  <Button variant="ghost" className="text-indigo-400 hover:text-indigo-300">
                    View All
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentEvents.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No events yet</p>
                ) : (
                  recentEvents.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                          {event.code?.slice(0, 2) || 'EV'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{event.event_name}</p>
                          <p className="text-sm text-gray-400">{event.school_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-indigo-600 text-white">
                          {event.attendee_count || 0} attendees
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">Code: {event.code}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Platform Health</CardTitle>
              <CardDescription className="text-gray-400">System status overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div>
                    <p className="text-green-400 font-medium">API Status</p>
                    <p className="text-2xl font-bold text-white">Operational</p>
                  </div>
                  <Activity className="h-8 w-8 text-green-400" />
                </div>
                <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div>
                    <p className="text-yellow-400 font-medium">Pending Payments</p>
                    <p className="text-2xl font-bold text-white">{stats?.pending_payments || 0}</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-yellow-400" />
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div>
                    <p className="text-blue-400 font-medium">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
